<template>
  <div class="emission-unit-parameters">
    <h3>{{ $t('emissionUnit.title') }}</h3>
    <p class="description">
      {{ $t('emissionUnit.description') }}
    </p>
    
    <div class="unit-options">
      <div class="radio-group">
        <label class="radio-option">
          <input
            type="radio"
            v-model="selectedUnit"
            value="tCO2e"
            @change="updateEmissionUnit"
          />
          <span class="radio-custom"></span>
          <div class="radio-content">
            <div class="radio-title">{{ $t('emissionUnit.tonnes.title') }}</div>
            <div class="radio-description">{{ $t('emissionUnit.tonnes.description') }}</div>
          </div>
        </label>

        <label class="radio-option">
          <input
            type="radio"
            v-model="selectedUnit"
            value="kgCO2e"
            @change="updateEmissionUnit"
          />
          <span class="radio-custom"></span>
          <div class="radio-content">
            <div class="radio-title">{{ $t('emissionUnit.kilograms.title') }}</div>
            <div class="radio-description">{{ $t('emissionUnit.kilograms.description') }}</div>
          </div>
        </label>

        <label class="radio-option">
          <input
            type="radio"
            v-model="selectedUnit"
            value="gCO2e"
            @change="updateEmissionUnit"
          />
          <span class="radio-custom"></span>
          <div class="radio-content">
            <div class="radio-title">{{ $t('emissionUnit.grams.title') }}</div>
            <div class="radio-description">{{ $t('emissionUnit.grams.description') }}</div>
          </div>
        </label>
      </div>
    </div>
    
    <div class="preview-section">
      <h4>{{ $t('emissionUnit.preview') }}</h4>
      <div class="preview-example">
        <span class="label">{{ $t('emissionUnit.displayExample') }} :</span>
        <span class="value">{{ getFormattedExample() }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'emissionUnitParameters',
  emits: ['updateCompany'],
  props: {
    propsCompany: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedUnit: 'tCO2e' // Valeur par défaut
    }
  },
  mounted() {
    // Initialiser avec la valeur de l'entreprise ou la valeur par défaut
    this.selectedUnit = this.propsCompany.emission_unit || 'tCO2e';
  },
  watch: {
    propsCompany: {
      handler(newCompany) {
        if (newCompany && newCompany.emission_unit) {
          this.selectedUnit = newCompany.emission_unit;
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    updateEmissionUnit() {
      // Créer une copie de l'entreprise avec la nouvelle unité
      const updatedCompany = {
        ...this.propsCompany,
        emission_unit: this.selectedUnit
      };
      
      // Émettre l'événement pour mettre à jour l'entreprise
      this.$emit('updateCompany', updatedCompany);
    },
    
    getFormattedExample() {
      // Exemple avec 1234.56 comme valeur de base en kg
      const exampleValueInKg = 1234.56;
      
      switch (this.selectedUnit) {
        case 'tCO2e':
          return `${(exampleValueInKg / 1000).toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} tCO2e`;
        case 'kgCO2e':
          return `${exampleValueInKg.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} kgCO2e`;
        case 'gCO2e':
          return `${(exampleValueInKg * 1000).toLocaleString('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })} gCO2e`;
        default:
          return `${(exampleValueInKg / 1000).toFixed(2)} tCO2e`;
      }
    }
  }
}
</script>

<style scoped>
.emission-unit-parameters {
  margin-bottom: 32px;
}

.emission-unit-parameters h3 {
  color: #1e293b;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.description {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.radio-option:hover {
  border-color: #cbd5e1;
  background-color: #f8fafc;
}

.radio-option:has(input:checked) {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.radio-option input[type="radio"] {
  display: none;
}

.radio-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  margin-top: 2px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.radio-option:has(input:checked) .radio-custom {
  border-color: #3b82f6;
}

.radio-option:has(input:checked) .radio-custom::after {
  content: '';
  width: 8px;
  height: 8px;
  background-color: #3b82f6;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.radio-content {
  flex: 1;
}

.radio-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.radio-description {
  font-size: 14px;
  color: #64748b;
}

.preview-section {
  margin-top: 24px;
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.preview-section h4 {
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
}

.preview-example {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-example .label {
  font-size: 14px;
  color: #64748b;
}

.preview-example .value {
  font-weight: 600;
  color: #059669;
  font-size: 16px;
}
</style>